import RAPIER from "@dimforge/rapier3d-simd-compat";
import * as THREE from "three";

export type PhysicsInitOptions = {
    gravity?: { x: number; y: number; z: number };
    tickRate?: number; // per second
};

export type PlayerControllerOptions = {
    radius?: number;
    height?: number;
    speedWalk?: number;
    speedRun?: number;
    jumpVelocity?: number;
};

export class PhysicsManager {
    private static _initPromise: Promise<void> | null = null;

    private _world: RAPIER.World | undefined;
    private _eventQueue: RAPIER.EventQueue | undefined;
    private _fixedTimeStep: number;
    private _accumulator = 0;

    // Simple player body & controller state
    private _playerBody: RAPIER.RigidBody | null = null;
    private _playerCollider: RAPIER.Collider | null = null;
    private _options: PlayerControllerOptions;
    private _onUpdatePlayerCamera?: (pos: THREE.Vector3) => void;
    private _groundBody: RAPIER.RigidBody | null = null;
    private _groundCollider: RAPIER.Collider | null = null;

    // Entity colliders for environment objects
    private _entityBodies: Map<string, { body: RAPIER.RigidBody; collider: RAPIER.Collider }> = new Map();

    private _readyPromise: Promise<void>;
    private _gravity: { x: number; y: number; z: number };
    private _isSolidFn: ((x: number, y: number, z: number) => boolean) | null =
        null;

    constructor(options: PhysicsInitOptions = {}) {
        this._gravity = options.gravity ?? { x: 0, y: -32, z: 0 };
        const tickRate = options.tickRate ?? 60;

        this._fixedTimeStep = 1 / tickRate;
        this._options = {
            radius: 0.35,
            height: 1.5,
            speedWalk: 4,
            speedRun: 8,
            jumpVelocity: 10,
        };

        if (!PhysicsManager._initPromise) {
            PhysicsManager._initPromise = RAPIER.init();
        }

        this._readyPromise = PhysicsManager._initPromise.then(() => {
            this._world = new RAPIER.World(this._gravity);
            this._eventQueue = new RAPIER.EventQueue(true);
        });
    }

    public ready(): Promise<void> {
        return this._readyPromise;
    }

    public setIsSolidQuery(fn: (x: number, y: number, z: number) => boolean) {
        this._isSolidFn = fn;
    }
    public getPlayerHalfHeight(): number {
        return Math.max(0.01, (this._options.height ?? 1.5) / 2);
    }

    public setPlayerUpdateCallback(cb: (pos: THREE.Vector3) => void) {
        this._onUpdatePlayerCamera = cb;
    }

    public createOrResetPlayer(
        initialPosition: THREE.Vector3,
        opts?: Partial<PlayerControllerOptions>
    ) {
        if (!this._world) return; // not ready yet
        this._options = { ...this._options, ...(opts ?? {}) };

        // Remove old
        if (this._playerCollider)
            this._world.removeCollider(this._playerCollider, true);
        if (this._playerBody) this._world.removeRigidBody(this._playerBody);

        const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
            .setTranslation(
                initialPosition.x,
                initialPosition.y,
                initialPosition.z
            )
            .lockRotations();
        this._playerBody = this._world.createRigidBody(bodyDesc);

        const halfHeight = Math.max(0.01, (this._options.height ?? 1.5) / 2);
        const radius = Math.max(0.01, this._options.radius ?? 0.35);
        const colliderDesc = RAPIER.ColliderDesc.capsule(
            halfHeight - radius,
            radius
        )
            .setFriction(0)
            .setRestitution(0)
            .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
        this._playerCollider = this._world.createCollider(
            colliderDesc,
            this._playerBody
        );
    }

    public addStaticTrimesh(
        vertices: Float32Array,
        indices: Uint32Array,
        position?: THREE.Vector3
    ) {
        if (!this._world) return null;
        const rb = this._world.createRigidBody(
            RAPIER.RigidBodyDesc.fixed().setTranslation(
                position?.x ?? 0,
                position?.y ?? 0,
                position?.z ?? 0
            )
        );
        const collider = RAPIER.ColliderDesc.trimesh(
            vertices,
            indices,
            undefined
        )
            .setFriction(0.8)
            .setRestitution(0);
        this._world.createCollider(collider, rb);
        return rb;
    }

    public addFlatGround(size: number = 2000, y: number = 0) {
        if (!this._world) return null;
        
        // Remove existing ground if present
        if (this._groundCollider) {
            this._world.removeCollider(this._groundCollider, true);
        }
        if (this._groundBody) {
            this._world.removeRigidBody(this._groundBody);
        }
        
        const rb = this._world.createRigidBody(
            RAPIER.RigidBodyDesc.fixed().setTranslation(0, y, 0)
        );
        const half = size / 2;
        const collider = RAPIER.ColliderDesc.cuboid(half, 0.5, half)
            .setFriction(0.9)
            .setRestitution(0);
        const colliderHandle = this._world.createCollider(collider, rb);
        
        // Store references
        this._groundBody = rb;
        this._groundCollider = colliderHandle;
        
        // Store top surface Y for simple ground-plane checks
        // Rapier cuboid half-height is 0.5, so top surface is y + 0.5
        (this as any)._flatGroundTopY = y + 0.5;
        try {
            (window as any).__WE_PM_PLANE_TOP_Y__ = (
                this as any
            )._flatGroundTopY;
        } catch {}
        return rb;
    }

    public updateGroundY(y: number) {
        if (!this._world) return;
        
        // If ground doesn't exist yet, create it
        if (!this._groundBody) {
            this.addFlatGround(4000, y);
            return;
        }
        
        // Update the rigid body translation immediately
        this._groundBody.setTranslation({ x: 0, y, z: 0 }, true);
        
        // Update stored top surface Y immediately (before physics step)
        // Rapier cuboid half-height is 0.5, so top surface is y + 0.5
        (this as any)._flatGroundTopY = y + 0.5;
        try {
            (window as any).__WE_PM_PLANE_TOP_Y__ = (
                this as any
            )._flatGroundTopY;
        } catch {}
    }

    public removeRigidBody(rb: RAPIER.RigidBody) {
        if (!this._world) return;
        this._world.removeRigidBody(rb);
    }

    public getPlayerPosition(): THREE.Vector3 | null {
        if (!this._playerBody) return null;
        const t = this._playerBody.translation();
        return new THREE.Vector3(t.x, t.y, t.z);
    }

    /**
     * Add a collider for an environment entity.
     * The bounding box center is used to properly position the collider relative to the entity position.
     */
    public addEntityCollider(
        entityId: string,
        position: THREE.Vector3,
        rotation: THREE.Euler,
        scale: THREE.Vector3,
        boundingBox: {
            width: number;
            height: number;
            depth: number;
            center: THREE.Vector3;
        }
    ): boolean {
        if (!this._world) return false;

        // Remove existing collider for this entity if any
        this.removeEntityCollider(entityId);

        // Calculate world position for collider
        // The bounding box center offset must be scaled with the entity scale
        const scaledCenter = boundingBox.center.clone().multiply(scale);
        const worldPos = position.clone().add(scaledCenter);

        // Convert Euler rotation to quaternion for RAPIER
        const quat = new THREE.Quaternion().setFromEuler(rotation);

        // Create fixed rigid body at the world position with rotation
        const bodyDesc = RAPIER.RigidBodyDesc.fixed()
            .setTranslation(worldPos.x, worldPos.y, worldPos.z)
            .setRotation({ x: quat.x, y: quat.y, z: quat.z, w: quat.w });
        const body = this._world.createRigidBody(bodyDesc);

        // Half extents for box collider (scaled by entity scale)
        const halfExtents = {
            x: (boundingBox.width * scale.x) / 2,
            y: (boundingBox.height * scale.y) / 2,
            z: (boundingBox.depth * scale.z) / 2,
        };

        // Create box collider
        const colliderDesc = RAPIER.ColliderDesc.cuboid(
            halfExtents.x,
            halfExtents.y,
            halfExtents.z
        )
            .setFriction(0.5)
            .setRestitution(0);

        const collider = this._world.createCollider(colliderDesc, body);

        // Store in map
        this._entityBodies.set(entityId, { body, collider });

        return true;
    }

    /**
     * Remove a collider for an environment entity.
     */
    public removeEntityCollider(entityId: string): boolean {
        if (!this._world) return false;

        const entityData = this._entityBodies.get(entityId);
        if (!entityData) return false;

        // Remove collider first, then body
        this._world.removeCollider(entityData.collider, true);
        this._world.removeRigidBody(entityData.body);

        this._entityBodies.delete(entityId);
        return true;
    }

    /**
     * Update an entity's collider (position, rotation, scale, or bounding box changed).
     * Implemented as remove + add for simplicity.
     */
    public updateEntityCollider(
        entityId: string,
        position: THREE.Vector3,
        rotation: THREE.Euler,
        scale: THREE.Vector3,
        boundingBox: {
            width: number;
            height: number;
            depth: number;
            center: THREE.Vector3;
        }
    ): boolean {
        // Simply remove and re-add
        return this.addEntityCollider(entityId, position, rotation, scale, boundingBox);
    }

    /**
     * Clear all entity colliders.
     */
    public clearAllEntityColliders(): void {
        if (!this._world) return;

        for (const [entityId, entityData] of this._entityBodies) {
            this._world.removeCollider(entityData.collider, true);
            this._world.removeRigidBody(entityData.body);
        }

        this._entityBodies.clear();
    }

    /**
     * Check if an entity has a collider registered.
     */
    public hasEntityCollider(entityId: string): boolean {
        return this._entityBodies.has(entityId);
    }

    /**
     * Get the number of entity colliders currently registered.
     */
    public getEntityColliderCount(): number {
        return this._entityBodies.size;
    }

    public step(
        deltaSeconds: number,
        input: {
            w?: boolean;
            a?: boolean;
            s?: boolean;
            d?: boolean;
            sh?: boolean;
            sp?: boolean;
            c?: boolean;
        },
        cameraYaw: number
    ) {
        if (!this._world || !this._eventQueue) return;
        this._accumulator += deltaSeconds;
        while (this._accumulator >= this._fixedTimeStep) {
            this._simulateFixed(input, cameraYaw);
            this._accumulator -= this._fixedTimeStep;
        }

        if (this._playerBody && this._onUpdatePlayerCamera) {
            const t = this._playerBody.translation();
            this._onUpdatePlayerCamera(new THREE.Vector3(t.x, t.y + 1.2, t.z));
        }
    }

    private _simulateFixed(
        input: {
            w?: boolean;
            a?: boolean;
            s?: boolean;
            d?: boolean;
            sh?: boolean;
            sp?: boolean;
            c?: boolean;
        },
        cameraYaw: number
    ) {
        if (!this._world) return;
        if (this._playerBody) {
            const lv = this._playerBody.linvel();
            let targetX = 0,
                targetZ = 0;

            const speed = input.sh
                ? this._options.speedRun ?? 8
                : this._options.speedWalk ?? 4;
            const sinYaw = Math.sin(cameraYaw);
            const cosYaw = Math.cos(cameraYaw);

            if (input.w) {
                targetX -= speed * sinYaw;
                targetZ -= speed * cosYaw;
            }
            if (input.s) {
                targetX += speed * sinYaw;
                targetZ += speed * cosYaw;
            }
            if (input.a) {
                targetX -= speed * cosYaw;
                targetZ += speed * sinYaw;
            }
            if (input.d) {
                targetX += speed * cosYaw;
                targetZ -= speed * sinYaw;
            }

            // Normalize
            const mag = Math.hypot(targetX, targetZ);
            if (mag > speed) {
                const f = speed / mag;
                targetX *= f;
                targetZ *= f;
            }

            // Basic voxel collision using external spatial hash if available
            const pos = this._playerBody.translation();
            const dt = this._fixedTimeStep;
            const radius = Math.max(0.01, this._options.radius ?? 0.35);
            const halfHeight = Math.max(
                0.01,
                (this._options.height ?? 1.5) / 2
            );
            const next = {
                x: pos.x + targetX * dt,
                y: pos.y + lv.y * dt,
                z: pos.z + targetZ * dt,
            };
            const isSolid = (x: number, y: number, z: number) => {
                try {
                    if (this._isSolidFn) return this._isSolidFn(x, y, z);
                    // fallback to window hook if present
                    // @ts-ignore
                    if (typeof (window as any).__WE_IS_SOLID__ === "function") {
                        // @ts-ignore
                        return (window as any).__WE_IS_SOLID__(x, y, z);
                    }
                } catch {}
                return false;
            };

            // Helper to sample a few points around capsule feet/head at a given Y plane
            const sampleXZSolid = (
                cx: number,
                cy: number,
                cz: number
            ): boolean => {
                const offsets: Array<[number, number]> = [
                    [0, 0],
                    [radius * 0.8, 0],
                    [-radius * 0.8, 0],
                    [0, radius * 0.8],
                    [0, -radius * 0.8],
                ];
                const by = Math.floor(cy);
                for (const [ox, oz] of offsets) {
                    const bx = Math.floor(cx + ox);
                    const bz = Math.floor(cz + oz);
                    if (isSolid(bx, by, bz)) return true;
                }
                return false;
            };

            // Resolve horizontal X, Z independently to avoid getting stuck on corners
            // Test at mid section of capsule - use current position for consistency
            const midY = pos.y;
            // Use current position (pos) for collision checks to avoid jitter when strafing (A/D)
            // This ensures consistent collision detection regardless of movement direction
            // Try move in X
            if (sampleXZSolid(next.x, midY, pos.z)) {
                targetX = 0;
                next.x = pos.x;
            }
            // Try move in Z
            if (sampleXZSolid(pos.x, midY, next.z)) {
                targetZ = 0;
                next.z = pos.z;
            }

            // Vertical collision: ground and ceiling
            let newVy = lv.y;
            const bottom = next.y - halfHeight;
            const top = next.y + halfHeight;
            const groundY = Math.floor(bottom - 0.01);
            const ceilingY = Math.floor(top + 0.01);

            let grounded = false;
            const planeTopY = (this as any)._flatGroundTopY;

            // Detect ground contact if a solid block is directly below within a small tolerance
            if (newVy <= 0) {
                const nearTopOfGround = bottom <= groundY + 1 + 0.08; // tolerance 8cm
                // Use next.x, next.z for ground support check to detect blocks at the position we're moving to
                const hasVoxelSupport = sampleXZSolid(next.x, groundY, next.z);

                // Improved ground plane detection with better tolerance for negative values
                // Check if player is at or near the plane top, accounting for velocity
                // Use a larger tolerance (0.2) to catch fast-moving players and account for negative values
                const planeTolerance = 0.2;
                let nearFlatPlane = false;
                let willHitPlane = false;

                if (typeof planeTopY === "number") {
                    const distanceToPlane = bottom - planeTopY;
                    // Check if player is currently near the plane (within tolerance above or slightly below)
                    nearFlatPlane = distanceToPlane <= planeTolerance && distanceToPlane >= -planeTolerance * 0.5;

                    // Also check if player is moving downward and will intersect or pass through the plane this frame
                    // This prevents falling through when moving fast, especially for negative ground planes
                    if (!nearFlatPlane) {
                        // Use current velocity (lv.y) before any modifications for prediction
                        const currentVy = lv.y;
                        if (currentVy < 0) { // Only check if moving downward
                            const predictedBottom = bottom + currentVy * dt;
                            // Check if we'll hit or pass through the plane this frame
                            // Allow some tolerance to catch fast-moving players
                            willHitPlane = predictedBottom <= planeTopY + planeTolerance;
                            // Also check if player is already below the plane (shouldn't happen, but catch it)
                            if (bottom < planeTopY - 0.1) {
                                willHitPlane = true; // Force correction if player is below plane
                            }
                        }
                    }
                }

                // Check for entity collider ground support using raycast
                // Cast a ray downward from the player's position to detect entity colliders
                let hasEntitySupport = false;
                let entityGroundY: number | null = null;
                if (this._world && this._entityBodies.size > 0) {
                    const rayOrigin = { x: next.x, y: pos.y, z: next.z };
                    const rayDir = { x: 0, y: -1, z: 0 };
                    // Cast ray downward, max distance is halfHeight + small tolerance
                    const maxToi = halfHeight + 0.15;

                    const ray = new RAPIER.Ray(rayOrigin, rayDir);
                    // Use castRay to find the closest intersection
                    // Exclude the player's own collider by checking the collider handle
                    const hit = this._world.castRay(
                        ray,
                        maxToi,
                        true, // solid
                        undefined, // query filter flags
                        undefined, // filter groups
                        this._playerCollider ? this._playerCollider : undefined, // exclude player collider
                        undefined, // exclude rigid body
                        (collider) => {
                            // Exclude player collider and ground collider from entity ground check
                            if (this._playerCollider && collider.handle === this._playerCollider.handle) {
                                return false;
                            }
                            if (this._groundCollider && collider.handle === this._groundCollider.handle) {
                                return false;
                            }
                            return true;
                        }
                    );

                    if (hit !== null) {
                        // Hit an entity collider - we have ground support
                        hasEntitySupport = true;
                        // Calculate the Y position of the hit point
                        entityGroundY = rayOrigin.y - hit.timeOfImpact;
                    }
                }

                if (nearTopOfGround && hasVoxelSupport) {
                    grounded = true;
                    newVy = 0;
                    next.y = groundY + 1 + halfHeight + 0.001; // small epsilon
                } else if (nearFlatPlane || willHitPlane) {
                    grounded = true;
                    newVy = 0;
                    // Ensure player is positioned exactly on top of the plane
                    // Use a small epsilon to prevent sinking
                    const targetY = planeTopY + halfHeight + 0.001;
                    // Always snap to plane if we detected collision (prevents falling through)
                    // But only move upward if player is below or at the plane
                    if (next.y <= targetY || nearFlatPlane || willHitPlane) {
                        next.y = targetY;
                    }
                } else if (hasEntitySupport && entityGroundY !== null) {
                    // Standing on an entity collider
                    grounded = true;
                    newVy = 0;
                    // Snap player to the top of the entity
                    const targetY = entityGroundY + halfHeight + 0.001;
                    next.y = targetY;
                }
            }
            // Ceiling check (moving up): stop upward motion if intersecting just below ceiling
            if (newVy > 0) {
                const nearBottomOfCeiling = top >= ceilingY - 0.08;
                if (
                    nearBottomOfCeiling &&
                    sampleXZSolid(next.x, ceilingY, next.z)
                ) {
                    newVy = 0;
                    next.y = ceilingY - halfHeight - 0.001;
                }
            }

            // Apply resolved velocity
            this._playerBody.setLinvel(
                { x: targetX, y: newVy, z: targetZ },
                true
            );
            // Apply positional snap if changed (prevents sinking)
            // When on flat plane and grounded, always set Y translation to maintain stability
            // planeTopY is already declared above, reuse it
            const isOnFlatPlane = typeof planeTopY === "number" && grounded;
            if (next.y !== pos.y || next.x !== pos.x || next.z !== pos.z || isOnFlatPlane) {
                // Only correct Y snap to avoid fighting integration for XZ
                // When on flat plane, always set Y to maintain exact position
                if (next.y !== pos.y || isOnFlatPlane) {
                    this._playerBody.setTranslation(
                        { x: pos.x, y: next.y, z: pos.z },
                        true
                    );
                }
            }

            // Jump only when grounded (prevents stuck-on-ground no-jump)
            if (input.sp && grounded) {
                this._playerBody.setLinvel(
                    {
                        x: targetX,
                        y: this._options.jumpVelocity ?? 10,
                        z: targetZ,
                    },
                    true
                );
            }

            // Expose diagnostics for animation system and debugging
            try {
                (window as any).__WE_PM_GROUNDED__ = grounded;
                (window as any).__WE_PM_VY__ = newVy;
            } catch {}
        }

        this._world.step(this._eventQueue!);
    }
}

export default PhysicsManager;
